import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, MapPin, Clock, Shield, CheckCircle, 
  Loader2, Search, ArrowRight, Users, Package, Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 49.99,
    locations: 500,
    shipments: 50,
    features: ['Access to 500+ locations', 'Basic tracking', 'Email support'],
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 149.99,
    locations: 1500,
    shipments: 200,
    features: ['Access to 1,500+ locations', 'Priority handling', 'Phone support', 'Insurance included'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 399.99,
    locations: 2500,
    shipments: 'Unlimited',
    features: ['Access to 2,500+ locations', 'Dedicated account manager', '24/7 support', 'Full insurance', 'Custom integrations'],
    popular: false
  }
];

const OfficeNetworkPage = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchZip, setSearchZip] = useState('');
  const { getAuthHeaders } = useAuth();

  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: ''
  });

  useEffect(() => {
    fetchSubscription();
    fetchLocations();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/office-network/subscription`, {
        headers: getAuthHeaders()
      });
      setSubscription(response.data);
    } catch (error) {
      // No subscription
    }
  };

  const fetchLocations = async (zip = '') => {
    try {
      const params = zip ? `?zip_code=${zip}` : '';
      const response = await axios.get(`${API}/office-network/locations${params}`, {
        headers: getAuthHeaders()
      });
      // API returns locations in response.data.locations
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations');
      setLocations([]);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/office-network/subscribe`, {
        plan: selectedPlan,
        ...formData
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Welcome to ${response.data.plan} plan!`);
      setSubscription(response.data);
      setActiveTab('locations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  const searchLocations = () => {
    fetchLocations(searchZip);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="office-network-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            Office Network
          </h1>
          <p className="text-slate-500 mt-1">Your secure mail network with 2,500+ locations</p>
        </div>
        {subscription && (
          <Badge className="bg-emerald-500 text-white">
            <CheckCircle className="w-3 h-3 mr-1" />
            {subscription.plan?.toUpperCase()} Plan Active
          </Badge>
        )}
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <Building2 className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-3xl font-bold">2,500+</p>
            <p className="text-sm opacity-80">Office Locations</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <Shield className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-3xl font-bold">Secure</p>
            <p className="text-sm opacity-80">Mail Handling</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <Clock className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-3xl font-bold">24/7</p>
            <p className="text-sm opacity-80">Access Available</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <Package className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-3xl font-bold">Drop & Go</p>
            <p className="text-sm opacity-80">Easy Pickup</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="plans" data-testid="tab-plans">
            <Users className="w-4 h-4 mr-2" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">
            <MapPin className="w-4 h-4 mr-2" />
            Locations
          </TabsTrigger>
          <TabsTrigger value="subscribe" data-testid="tab-subscribe">
            <Building2 className="w-4 h-4 mr-2" />
            Subscribe
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative cursor-pointer transition-all hover:shadow-xl ${
                  selectedPlan === plan.id ? 'ring-2 ring-blue-500' : ''
                } ${plan.popular ? 'border-blue-500' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-slate-500">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{plan.locations}+</p>
                      <p className="text-xs text-slate-500">Locations</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{plan.shipments}</p>
                      <p className="text-xs text-slate-500">Shipments/mo</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className="w-full" 
                    variant={selectedPlan === plan.id ? 'default' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan.id);
                      setActiveTab('subscribe');
                    }}
                  >
                    {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Find Nearby Locations</CardTitle>
              <CardDescription>Search for office network locations near you</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <Input
                    placeholder="Enter ZIP code"
                    value={searchZip}
                    onChange={(e) => setSearchZip(e.target.value)}
                    maxLength={5}
                    data-testid="location-search"
                  />
                </div>
                <Button onClick={searchLocations}>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>

              <div className="space-y-4">
                {locations.map((location) => (
                  <div 
                    key={location.id}
                    className="p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{location.name}</h3>
                        <p className="text-sm text-slate-500">
                          {location.address}, {location.city}, {location.state} {location.zip}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-emerald-600">
                            <Clock className="w-4 h-4" />
                            {location.hours}
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <MapPin className="w-4 h-4 mr-2" />
                        Directions
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribe Tab */}
        <TabsContent value="subscribe" className="space-y-6">
          {subscription ? (
            <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10">
              <CardContent className="pt-6 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold mb-2">You're Subscribed!</h2>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  You have access to {subscription.locations_access}+ office locations.
                </p>
                <Badge className="text-lg px-4 py-2">{subscription.plan?.toUpperCase()} Plan</Badge>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Subscribe to Office Network</CardTitle>
                <CardDescription>
                  {selectedPlan ? (
                    <span>Selected plan: <strong>{PLANS.find(p => p.id === selectedPlan)?.name}</strong> - ${PLANS.find(p => p.id === selectedPlan)?.price}/month</span>
                  ) : (
                    'Please select a plan first'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Your Company"
                      data-testid="company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      placeholder="Full name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      placeholder="you@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Billing Address *</Label>
                  <Input
                    value={formData.billing_address}
                    onChange={(e) => setFormData({...formData, billing_address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={formData.billing_city}
                      onChange={(e) => setFormData({...formData, billing_city: e.target.value})}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Input
                      value={formData.billing_state}
                      onChange={(e) => setFormData({...formData, billing_state: e.target.value})}
                      placeholder="CA"
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP *</Label>
                    <Input
                      value={formData.billing_zip}
                      onChange={(e) => setFormData({...formData, billing_zip: e.target.value})}
                      placeholder="90001"
                      maxLength={5}
                    />
                  </div>
                </div>

                <Button 
                  className="w-full btn-press" 
                  size="lg"
                  onClick={handleSubscribe}
                  disabled={loading || !selectedPlan}
                  data-testid="subscribe-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Subscribe - ${PLANS.find(p => p.id === selectedPlan)?.price || 0}/month
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OfficeNetworkPage;
